"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { Loader2 } from "lucide-react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { firebaseManager } from "@/lib/firebase";
import { useAuth } from "@/lib/auth/AuthContext";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useTenantSettings, useFeatureFlag } from "@/lib/tenant/TenantSettingsProvider";
import { BrandedHeader } from "@/components/branded/headers/BrandedHeader";
import { PageNavigation } from "@/components/layout/PageNavigation";
import { useProfileData } from "./hooks/useProfileData";
import ActivityTab from "./partials/ActivityTab";
import SettingsTab from "./partials/SettingsTab";
interface ProfilePageProps {
  locale: string;
}
type Tab = "activity" | "settings";

/**
 * Unified user profile page with team-page-style layout.
 * BrandedHeader with avatar + name, sticky tab navigation, tab content.
 */
export function ProfilePage(t0) {
  const $ = _c(67);
  const {
    locale
  } = t0;
  const router = useRouter();
  const labels = useLabels();
  const {
    isMultiLang
  } = useTenantSettings();
  const isAuthFeatureEnabled = useFeatureFlag("authentication");
  const {
    user,
    loading: authLoading,
    isAuthEnabled,
    signOut
  } = useAuth();
  const sdk = useFansUnitedSDK();
  const {
    profileData,
    stats,
    isLoading,
    reloadProfile
  } = useProfileData();
  const [activeTab, setActiveTab] = useState("activity");
  let t1;
  if ($[0] !== labels.auth?.profile) {
    t1 = labels.auth?.profile || {} as Record<string, string>;
    $[0] = labels.auth?.profile;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  if ($[2] !== labels.auth?.myActivity) {
    t2 = labels.auth?.myActivity || {} as Record<string, string>;
    $[2] = labels.auth?.myActivity;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const activityLabels = t2;
  const basePath = isMultiLang && locale ? `/${locale}` : "";
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const hash = window.location.hash.slice(1) as Tab;
      const validTabs = ["activity", "settings"];
      if (hash && validTabs.includes(hash)) {
        setActiveTab(hash);
      }
    };
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = tabId => {
      setActiveTab(tabId);
      window.history.pushState(null, "", `#${tabId}`);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleTabChange = t5;
  let t6;
  let t7;
  if ($[7] !== authLoading || $[8] !== basePath || $[9] !== isAuthEnabled || $[10] !== router || $[11] !== user) {
    t6 = () => {
      if (!authLoading && (!user || user.isAnonymous) && isAuthEnabled) {
        router.push(`${basePath}/sign-in?returnUrl=${encodeURIComponent(`${basePath}/profile`)}`);
      }
    };
    t7 = [user, authLoading, isAuthEnabled, router, basePath];
    $[7] = authLoading;
    $[8] = basePath;
    $[9] = isAuthEnabled;
    $[10] = router;
    $[11] = user;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== reloadProfile || $[15] !== sdk) {
    t8 = async formData => {
      if (!sdk) {
        throw new Error("SDK not available");
      }
      let updateChain = sdk.profile.getOwn().setName(formData.name).setEmail(formData.email);
      if (formData.nickname) {
        updateChain = updateChain.setNickname(formData.nickname);
      }
      if (formData.countryId) {
        updateChain = updateChain.setCountry(formData.countryId);
      }
      if (formData.gender) {
        updateChain = updateChain.setGender(formData.gender);
      }
      await updateChain.update();
      await reloadProfile();
    };
    $[14] = reloadProfile;
    $[15] = sdk;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleProfileSave = t8;
  let t9;
  if ($[17] !== t) {
    t9 = async passwords => {
      const auth = firebaseManager.getAuth();
      if (!auth?.currentUser) {
        throw new Error("User not authenticated");
      }
      const firebaseUser = auth.currentUser;
      ;
      try {
        const credential = EmailAuthProvider.credential(firebaseUser.email, passwords.currentPassword);
        await reauthenticateWithCredential(firebaseUser, credential);
        await updatePassword(firebaseUser, passwords.newPassword);
      } catch (t10) {
        const err = t10;
        const firebaseError = err as {
          code?: string;
          message?: string;
        };
        if (firebaseError.code === "auth/wrong-password") {
          throw new Error(t.wrongPassword || "Current password is incorrect");
        } else {
          if (firebaseError.code === "auth/weak-password") {
            throw new Error(t.weakPassword || "New password is too weak. Please choose a stronger password");
          } else {
            if (firebaseError.code === "auth/requires-recent-login") {
              throw new Error(t.requiresRecentLogin || "Please sign out and sign in again before changing your password");
            } else {
              throw new Error(err instanceof Error ? err.message : "Failed to change password");
            }
          }
        }
      }
    };
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handlePasswordChange = t9;
  let t10;
  if ($[19] !== basePath || $[20] !== router || $[21] !== signOut) {
    t10 = async () => {
      await signOut();
      router.push(basePath || "/");
    };
    $[19] = basePath;
    $[20] = router;
    $[21] = signOut;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const handleSignOut = t10;
  if (!isAuthFeatureEnabled || !isAuthEnabled) {
    let t11;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="flex min-h-screen items-center justify-center px-4 py-12"><div className="text-center text-muted-foreground">Authentication is not configured for this site.</div></div>;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    return t11;
  }
  if (authLoading || isLoading) {
    let t11;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Loader2 className="h-8 w-8 animate-spin text-primary" />;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    const t12 = t.loadingProfile || "Loading profile...";
    let t13;
    if ($[25] !== t12) {
      t13 = <div className="flex min-h-screen items-center justify-center px-4 py-12"><div className="flex flex-col items-center gap-3">{t11}<p className="text-sm text-muted-foreground">{t12}</p></div></div>;
      $[25] = t12;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    return t13;
  }
  if (!user || user.isAnonymous) {
    return null;
  }
  const displayName = profileData?.name || user.displayName || user.email || "";
  const avatarUrl = profileData?.avatar || user.photoURL || undefined;
  let t11;
  if ($[27] !== stats) {
    t11 = stats ? `${(stats.points ?? 0).toLocaleString()} pts` : "";
    $[27] = stats;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const tierSubtitle = t11;
  const t12 = activityLabels.title || "Activity";
  let t13;
  if ($[29] !== t12) {
    t13 = {
      id: "activity",
      label: t12
    };
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = t.settings || "Settings";
  let t15;
  if ($[31] !== t14) {
    t15 = {
      id: "settings",
      label: t14
    };
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t13 || $[34] !== t15) {
    t16 = [t13, t15];
    $[33] = t13;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const tabs = t16;
  let t17;
  if ($[36] !== avatarUrl || $[37] !== displayName || $[38] !== tierSubtitle) {
    t17 = <BrandedHeader title={displayName} subtitle={tierSubtitle} imageUrl={avatarUrl} />;
    $[36] = avatarUrl;
    $[37] = displayName;
    $[38] = tierSubtitle;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== activeTab || $[41] !== tabs) {
    t18 = <PageNavigation options={tabs} activeId={activeTab} onChange={handleTabChange} sticky={true} />;
    $[40] = activeTab;
    $[41] = tabs;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== activeTab || $[44] !== activityLabels || $[45] !== stats) {
    t19 = activeTab === "activity" && stats && <ActivityTab stats={stats} labels={activityLabels} />;
    $[43] = activeTab;
    $[44] = activityLabels;
    $[45] = stats;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== activeTab || $[48] !== stats) {
    t20 = activeTab === "activity" && !stats && <div className="rounded-lg border border-border bg-card p-8 text-center"><p className="text-muted-foreground">No activity data available yet.</p></div>;
    $[47] = activeTab;
    $[48] = stats;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== activeTab || $[51] !== basePath || $[52] !== handlePasswordChange || $[53] !== handleProfileSave || $[54] !== handleSignOut || $[55] !== profileData || $[56] !== reloadProfile || $[57] !== t) {
    t21 = activeTab === "settings" && profileData && <SettingsTab profileData={profileData} onSave={handleProfileSave} onPasswordChange={handlePasswordChange} onAvatarChanged={reloadProfile} onSignOut={handleSignOut} labels={t} basePath={basePath} />;
    $[50] = activeTab;
    $[51] = basePath;
    $[52] = handlePasswordChange;
    $[53] = handleProfileSave;
    $[54] = handleSignOut;
    $[55] = profileData;
    $[56] = reloadProfile;
    $[57] = t;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== t19 || $[60] !== t20 || $[61] !== t21) {
    t22 = <div className="mx-auto max-w-350 px-4 py-8">{t19}{t20}{t21}</div>;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t17 || $[64] !== t18 || $[65] !== t22) {
    t23 = <div className="min-h-screen">{t17}{t18}{t22}</div>;
    $[63] = t17;
    $[64] = t18;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  return t23;
}